<template>
    <ui-modal>
        <template #header>
            <h3 class="mb-1 text-center" v-text="configTariffTitle" />
            <h5 class="mb-0 text-center" v-text="'Wybór sklepów i konfiguracja'" />
        </template>

        <accordion
            header-class="drs-selected-tariff__header"
            class="drs-selected-tariff cursor-default"
            :is-active="expandedTariffId === selectedTariff.id"
        >
            <template #header>
                <div class="text-left">
                    <h4 class="drs-selected-tariff__name mb-0">
                        {{ selectedTariff.name }}
                    </h4>
                    <p class="text-md text-primary mb-0">
                        Abonament {{ $options.TIMELINES_ADJECTIVES[selectedTariff.timeline] }} za sklep
                    </p>
                </div>
            </template>
            <template #expand-button>
                <the-btn
                    class="btn-outline-custom"
                    text="Wybierz sklepy"
                    :icon="expandedTariffId === selectedTariff.id ? 'ArrowUp' : 'ArrowDown'"
                    @click="expandedTariffId = expandedTariffId === selectedTariff.id ? null : selectedTariff.id"
                />
            </template>
            <template #body>
                <!--
                <a-switch
                    disabled
                    class="mb-3"
                    text="Jednakowa konfiguracja dla wybranych sklepów"
                    @change="equalPrices = !equalPrices"
                />
                 -->

                <mass-selection-buttons
                    class="mb-4"
                    :value="isAllLocationsSelected"
                    @select="toggleAllLocations"
                />

                <tariff-shop
                    v-for="shop in availableShopsToSelect"
                    :key="shop.id"
                    :shop="shop"
                    :tariff="selectedTariff"
                    :expanded-tariff-id="expandedTariffId"
                    :expanded-shop-id="expandedShopId"
                    @expand-shop="expandedShopId = expandedShopId === $event ? null : $event"
                />

            </template>
        </accordion>

        <recap-table />

        <template #footer>
            <the-btn
                class="btn-outline-custom"
                text="Wróć"
                @click="onReject"
            />
            <the-btn
                class="btn-secondary"
                text="Zobacz podsumowanie"
                :disabled="btnState.disabled"
                v-tooltip="btnState.tooltip"
                @click="goToSummary"
            />
        </template>
    </ui-modal>
</template>

<script>
import TariffShop from './TariffShop.vue'
import RecapTable from './RecapTable.vue'
import { mapActions, mapGetters } from 'vuex'
import cloneDumb from '@/helpers/cloneDumb'
import { CONFIG_MODES } from '@/store/modules/drs/tariffs.module'
import { TIMELINES_ADJECTIVES } from '@/utils/constants/drs/tariffs'

export default {
    TIMELINES_ADJECTIVES,

    data: () => ({
        expandedTariffId: null,
        expandedShopId: null
    }),

    computed: {
        ...mapGetters('drsTariffs', [
            'tariffs',
            'selectedTariff',
            'configTariffTitle',
            'configMode',
            'availableShops',
            'tariffsCopy',
            'isCartLoading'
        ]),

        isFirstOpen() {
            // If there is only one selected tariff, make it opened
            return !!this.selectedTariff
        },

        userId() {
            return this.$store.getters.getUser.id
        },

        activeTariff() {
            return this.selectedTariff?.id === this.expandedTariffId ? this.selectedTariff : null
        },

        isAllLocationsSelected() {
            return ((this.activeTariff || {}).locations || []).every(el => el.selected)
        },

        atLeastOneLocationSelected() {
            return this.selectedTariff?.locations.some(el => el.selected)
        },

        availableShopsToSelect () {
            if (this.configMode === CONFIG_MODES.EDIT) {
                return this.availableShops(this.selectedTariff?.locations[0].id)
            }

            const availableShops = this.availableShops(null)

            return (this.selectedTariff?.locations || []).filter(location =>
                location.selected || availableShops.some(shop => shop.id === location.id)
            )
        },

        areChangesMade() {
            if (!this.tariffsCopy) {
                return false
            }
            /**
             * Computed checks if user made changes in configuration comparing current selected tariffs
             * with the copy made at the moment of entering edit mode. If they are different,
             * it means that user made some changes.
             */
            const firstSelectedLoc = JSON.parse(JSON.stringify(this.selectedTariff?.locations[0] || {}))
            const firstCopyLoc = JSON.parse(JSON.stringify(this.tariffsCopy.locations[0] || {}))
            return this.selectedTariff?.id !== this.tariffsCopy.id ||
                firstSelectedLoc.additionalDevices !== firstCopyLoc.additionalDevices
        },

        btnState() {
            if (this.isCartLoading) {
                return {
                    disabled: true,
                    tooltip: null
                }
            }

            if (!this.atLeastOneLocationSelected) {
                return {
                    disabled: true,
                    tooltip: 'Musisz wybrać przynajmniej jeden sklep, aby przejść dalej'
                }
            }

            if (this.configMode === CONFIG_MODES.EDIT && !this.areChangesMade) {
                return {
                    disabled: true,
                    tooltip: 'Przycisk uaktywni się po wprowadzeniu zmian w konfiguracji'
                }
            }

            return {
                disabled: false,
                tooltip: null
            }
        }

    },

    async created() {
        // If there is only one selected tariff, make it opened
        this.expandedTariffId = this.selectedTariff?.id

        // If there is only one selected shop, make it opened
        this.expandedShopId = this.selectedTariff?.locations.filter(l => l.selected).length === 1
            ? this.selectedTariff?.locations.find(l => l.selected).id
            : null
    },

    methods: {
        ...mapActions('drsTariffs', ['selectTariff']),

        goToSummary() {
            if (this.btnState.disabled) {
                return
            }

            this.$store.dispatch('openModal', {
                componentPath: 'drs/configuration/ConfigurationSummary'
            })
        },

        selectAllLocations(value) {
            this.$store.dispatch('selectAllLocations', value)
        },

        toggleAllLocations(value) {
            const tariff = cloneDumb(this.selectedTariff)

            for (const location of tariff.locations) {
                location.selected = value
            }

            this.$store.commit('drsTariffs/UPDATE_TARIFFS', tariff)
        },

        onReject() {
            this.$store.dispatch('openModal', {
                componentPath: 'drs/configuration/SelectTariff',
                args: {
                    noFetch: true
                }
            })
        }
    },

    components: {
        TariffShop,
        RecapTable
    }
}
</script>

<style lang="scss">
@import '../../../styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.drs-selected-tariff {
    border: 1px solid var(--color-gray-200);
    border-radius: 6px;
    text-align: left;

    &__name {
        font-size: 1.25rem;
    }

    @include media-breakpoint-down(md) {
        &__header {
            flex-wrap: wrap;

            button {
                width: 100%;
            }
        }
    }
}
</style>